import axios from "axios";

const api = axios.create({
  baseURL: "/api",
});

api.interceptors.request.use((config) => {
  const token = localStorage.getItem("token");
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  const lang = localStorage.getItem("app_language");
  if (lang) {
    config.headers["Accept-Language"] = lang;
  }
  return config;
});

api.interceptors.response.use(
  (res) => res,
  (err) => {
    const isAuthRoute = err.config?.url?.includes("/auth/login") || err.config?.url?.includes("/auth/register");
    const isDistributorPage = window.location.pathname.startsWith("/distributor");
    if (err.response?.status === 401 && !isAuthRoute && !isDistributorPage) {
      localStorage.removeItem("token");
      localStorage.removeItem("user");
      window.location.href = "/login";
    }
    return Promise.reject(err);
  }
);

// Config
export const getAppConfig = () => api.get("/config");

// Auth
export const login = (data) => api.post("/auth/login", data);
export const register = (data) => api.post("/auth/register", data);
export const getMe = () => api.get("/auth/me");
export const updateMe = (data) => api.put("/auth/me", data);
export const forgotPassword = (data) => api.post("/auth/forgot-password", data);
export const resetPassword = (data) => api.post("/auth/reset-password", data);

// Profiles
export const getProfiles = () => api.get("/profiles");
export const getProfile = (slug) => api.get(`/profiles/${slug}`);
export const createProfile = (data) => api.post("/profiles", data);
export const updateProfile = (slug, data) => api.put(`/profiles/${slug}`, data);
export const deleteProfile = (slug) => api.delete(`/profiles/${slug}`);
export const getProfileStorage = (slug) => api.get(`/profiles/${slug}/storage`);
export const uploadProfileImage = (slug, formData, { onUploadProgress, signal } = {}) =>
  api.post(`/profiles/${slug}/image`, formData, {
    headers: { "Content-Type": "multipart/form-data" },
    onUploadProgress,
    signal,
  });

// Blocks
export const getBlocks = (slug) => api.get(`/blocks/profile/${slug}`);
export const createBlock = (slug, data) =>
  api.post(`/blocks/profile/${slug}`, data);
export const updateBlock = (blockId, data) => api.put(`/blocks/${blockId}`, data);
export const deleteBlock = (blockId) => api.delete(`/blocks/${blockId}`);
export const reorderBlocks = (slug, order) =>
  api.put(`/blocks/reorder/${slug}`, { order });
export const uploadBlockMedia = (blockId, formData, { onUploadProgress, signal } = {}) =>
  api.post(`/blocks/${blockId}/media`, formData, {
    headers: { "Content-Type": "multipart/form-data" },
    onUploadProgress,
    signal,
  });
export const deleteBlockMedia = (blockId) =>
  api.delete(`/blocks/${blockId}/media`);

// Contacts
export const addContactItem = (blockId, data) =>
  api.post(`/blocks/${blockId}/contacts`, data);
export const updateContactItem = (itemId, data) =>
  api.put(`/blocks/contacts/${itemId}`, data);
export const deleteContactItem = (itemId) =>
  api.delete(`/blocks/contacts/${itemId}`);

// Social
export const addSocialLink = (blockId, data) =>
  api.post(`/blocks/${blockId}/social`, data);
export const updateSocialLink = (linkId, data) =>
  api.put(`/blocks/social/${linkId}`, data);
export const deleteSocialLink = (linkId) =>
  api.delete(`/blocks/social/${linkId}`);

// Media
export const getGalleries = (slug) =>
  api.get(`/media/galleries/${slug}`);
export const createGallery = (slug, data) =>
  api.post(`/media/galleries/${slug}`, data);
export const updateGallery = (galleryId, data) =>
  api.put(`/media/galleries/edit/${galleryId}`, data);
export const deleteGallery = (galleryId) =>
  api.delete(`/media/galleries/edit/${galleryId}`);
export const uploadMedia = (galleryId, formData, { onUploadProgress, signal } = {}) =>
  api.post(`/media/upload/${galleryId}`, formData, {
    headers: { "Content-Type": "multipart/form-data" },
    onUploadProgress,
    signal,
  });
export const updateMediaItem = (itemId, data) =>
  api.put(`/media/items/${itemId}`, data);
export const deleteMediaItem = (itemId) => api.delete(`/media/items/${itemId}`);
export const reorderMediaItems = (galleryId, itemIds) =>
  api.put(`/media/galleries/${galleryId}/reorder`, { item_ids: itemIds });

// Cards
export const linkCard = (code, profileId) =>
  api.post(`/c/${code}/link`, { profile_id: profileId });
export const getProfileCards = (slug) => api.get(`/profiles/${slug}/cards`);
export const unlinkCardUser = (code) => api.post(`/c/${code}/unlink`);
export const getMyCards = () => api.get("/c/my-cards");
export const relinkCard = (code, profileId) =>
  api.post(`/c/${code}/relink`, { profile_id: profileId });
export const updateCardLabel = (code, label) =>
  api.put(`/c/${code}/label`, { label });

// Organizations
export const getOrganizations = () => api.get("/organizations");
export const createOrganization = (data) => api.post("/organizations", data);
export const getOrganization = (orgId) => api.get(`/organizations/${orgId}`);
export const updateOrganization = (orgId, data) =>
  api.put(`/organizations/${orgId}`, data);
export const deleteOrganization = (orgId) =>
  api.delete(`/organizations/${orgId}`);
export const uploadOrgLogo = (orgId, formData) =>
  api.post(`/organizations/${orgId}/logo`, formData, {
    headers: { "Content-Type": "multipart/form-data" },
  });
export const getOrgProfiles = (orgId) =>
  api.get(`/organizations/${orgId}/profiles`);
export const createOrgProfile = (orgId, data) =>
  api.post(`/organizations/${orgId}/profiles`, data);

// Templates
export const getTemplate = (orgId) => api.get(`/templates/org/${orgId}`);
export const updateTemplate = (orgId, data) =>
  api.put(`/templates/org/${orgId}`, data);
export const uploadTemplateCover = (orgId, formData) =>
  api.post(`/templates/org/${orgId}/image`, formData, {
    headers: { "Content-Type": "multipart/form-data" },
  });
export const getTemplateBlocks = (orgId) =>
  api.get(`/templates/org/${orgId}/blocks`);
export const createTemplateBlock = (orgId, data) =>
  api.post(`/templates/org/${orgId}/blocks`, data);
export const updateTemplateBlock = (blockId, data) =>
  api.put(`/templates/blocks/${blockId}`, data);
export const deleteTemplateBlock = (blockId) =>
  api.delete(`/templates/blocks/${blockId}`);
export const reorderTemplateBlocks = (orgId, order) =>
  api.put(`/templates/org/${orgId}/blocks/reorder`, { order });

// Profile Reset
export const resetProfile = (slug, mode) =>
  api.post(`/profiles/${slug}/reset`, { mode });

// Public
export const getPublicProfile = (slug) => api.get(`/p/${slug}`);
export const createProfilePayment = (slug, data) => api.post(`/p/${slug}/pay`, data);
export const notifyPwaAttempt = (slug) => api.post(`/p/${slug}/pwa-attempt`).catch(() => {});

// Events
function getDeviceId() {
  let id = localStorage.getItem("bitsness_device_id");
  if (!id) {
    id = crypto.randomUUID();
    localStorage.setItem("bitsness_device_id", id);
  }
  return id;
}

export const logProfileEvent = (slug, event_type, event_data = {}) =>
  api.post(`/events/${slug}/event`, {
    event_type,
    event_data: { ...event_data, device_id: getDeviceId() },
  }).catch(() => {});
export const getProfileStats = (slug, params) =>
  api.get(`/events/${slug}/stats`, { params });

// Share
export const getShareUrl = (slug) => api.get(`/share/url/${slug}`);

// Leads / CRM
export const submitLead = (slug, data) => api.post(`/p/${slug}/lead`, data);
export const getLeads = (slug, params) =>
  api.get(`/profiles/${slug}/leads`, { params });
export const updateLead = (slug, leadId, data) =>
  api.patch(`/profiles/${slug}/leads/${leadId}`, data);
export const deleteLead = (slug, leadId) =>
  api.delete(`/profiles/${slug}/leads/${leadId}`);

// Reviews
export const submitReview = (slug, data) => api.post(`/p/${slug}/review`, data);
export const checkReview = (slug, visitorId) =>
  api.get(`/p/${slug}/review/check`, { params: { visitor_id: visitorId } });
export const getReviews = (slug, params) =>
  api.get(`/profiles/${slug}/reviews`, { params });

// Push subscriptions (owner)
export const subscribePush = (subscription) =>
  api.post("/push/subscribe", { subscription });
export const unsubscribePush = (endpoint) =>
  api.post("/push/unsubscribe", { endpoint });

// Visitor push subscriptions (public)
export const visitorPushSubscribe = (slug, subscription) =>
  api.post(`/p/${slug}/push/subscribe`, { subscription });
export const visitorPushUnsubscribe = (slug, endpoint) =>
  api.post(`/p/${slug}/push/unsubscribe`, { endpoint });
export const visitorPushCheck = (slug, endpoint) =>
  api.post(`/p/${slug}/push/check`, { endpoint });

// Profile notifications (public bell)
export const getProfileNotifications = (slug, limit = 20) =>
  api.get(`/p/${slug}/notifications`, { params: { limit } });

// Profile notifications (owner)
export const getOwnerNotifications = (slug) =>
  api.get(`/profiles/${slug}/notifications`);
export const sendNotification = (slug, data) =>
  api.post(`/profiles/${slug}/notifications`, data);
export const updateNotification = (slug, id, data) =>
  api.patch(`/profiles/${slug}/notifications/${id}`, data);
export const deleteNotification = (slug, id) =>
  api.delete(`/profiles/${slug}/notifications/${id}`);

// Payments
export const getPlans = (tier) => api.get("/payments/plans", { params: tier ? { tier } : {} });
export const getPaymentPublicKey = () => api.get("/payments/public-key");
export const createPaymentPreference = (data) =>
  api.post("/payments/create-preference", data);
export const createPaymentSubscription = (data) =>
  api.post("/payments/create-subscription", data);
export const processInlinePayment = (data) =>
  api.post("/payments/process-payment", data);
export const processInlineSubscription = (data) =>
  api.post("/payments/process-subscription", data);
export const createCardToken = (data) =>
  api.post("/payments/create-card-token", data);
export const getMyPayments = () => api.get("/payments/my-payments");
export const getPaymentStatus = (id) => api.get(`/payments/status/${id}`);

// Appointments (owner)
export const getAppointments = (slug, params) =>
  api.get(`/appointments/profile/${slug}`, { params });
export const updateAppointment = (id, data) =>
  api.put(`/appointments/${id}`, data);
export const downloadAppointmentIcal = (id) =>
  api.get(`/appointments/${id}/ical`, { responseType: "blob" });

// Appointments (public)
export const getAvailableSlots = (slug, params) =>
  api.get(`/appointments/public/${slug}/slots`, { params });
export const bookAppointment = (slug, data) =>
  api.post(`/appointments/public/${slug}/book`, { ...data, device_id: getDeviceId() });
export const getVisitorAppointments = (slug) =>
  api.get(`/appointments/public/${slug}/my-appointments`, { params: { device_id: getDeviceId() } });
export const cancelVisitorAppointment = (slug, id) =>
  api.post(`/appointments/public/${slug}/cancel/${id}`, { device_id: getDeviceId() });
export const getAppointmentIcalUrl = (slug, id) =>
  `/api/appointments/public/${slug}/ical/${id}?device_id=${getDeviceId()}`;

// Distributor portal API
const distApi = axios.create({ baseURL: "/api/distributor" });

distApi.interceptors.request.use((config) => {
  const token = localStorage.getItem("distributor_token");
  if (token) config.headers.Authorization = `Bearer ${token}`;
  const lang = localStorage.getItem("app_language");
  if (lang) config.headers["Accept-Language"] = lang;
  return config;
});

distApi.interceptors.response.use(
  (res) => res,
  (err) => {
    if (err.response?.status === 401 && !err.config?.url?.includes("/auth/login")) {
      localStorage.removeItem("distributor_token");
      localStorage.removeItem("distributor");
      window.location.href = "/distributor/login";
    }
    return Promise.reject(err);
  }
);

export const distributorLogin = (data) => distApi.post("/auth/login", data);
export const distributorMe = () => distApi.get("/auth/me");
export const distributorGetCards = () => distApi.get("/cards");
export const distributorLookupCard = (code) => distApi.get(`/cards/lookup/${encodeURIComponent(code)}`);
export const distributorMarkSold = (id, data) => distApi.post(`/cards/${id}/sold`, data);
export const distributorGetCardHistory = (id) => distApi.get(`/cards/${id}/history`);

export default api;
